import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { appBannerAPI } from '../../../services/api';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import { resolveImageUrl } from '../../../utils/imageUtils';
import { Plus, Search, Trash2, Edit, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import AppBannerForm from './AppBannerForm';
import './AppBannerManager.css';

const AppBannerManager = ({ initialView = 'list' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [view, setView] = useState(initialView);

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (view === 'list') {
      fetchBanners();
    } else if (view === 'edit' && id) {
      fetchBannerDetail(id);
    } else if (view === 'add') {
      setSelectedBanner(null);
    }
  }, [view, id]);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await appBannerAPI.getBanners();
      // Handle various response structures
      const responseData = response.data?.data || response.data;
      if (Array.isArray(responseData)) {
        setBanners(responseData);
      } else if (responseData?.banners && Array.isArray(responseData.banners)) {
        setBanners(responseData.banners);
      }
    } catch (error) {
      showErrorToast(t('error.fetchFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBannerDetail = async (bannerId) => {
    setLoading(true);
    try {
      const response = await appBannerAPI.getBanner(bannerId);
      // Handle the direct data response structure provided by user
      const responseData = response.data?.data || response.data;
      if (responseData && (responseData.id || responseData.title)) {
        setSelectedBanner(responseData);
      }
    } catch (error) {
      showErrorToast(t('error.fetchFailed'));
      navigate('/admin/dashboard/cms/banners');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner) => {
    navigate(`/admin/dashboard/cms/banners/edit/${banner.id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('common.confirmDelete'))) {
      try {
        await appBannerAPI.deleteBanner(id);
        showSuccessToast(t('success.deleted'));
        fetchBanners();
      } catch (error) {
        showErrorToast(t('error.deleteFailed'));
      }
    }
  };

  const handleSuccess = () => {
    navigate('/admin/dashboard/cms/banners');
    fetchBanners();
  };

  const handleCancel = () => {
    navigate('/admin/dashboard/cms/banners');
  };

  const filteredBanners = banners.filter(b =>
    (b.title_en || b.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.title_ar || b.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.country_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (view === 'add' || view === 'edit') {
    if (loading && view === 'edit') {
      return (
        <div className="ab-loading-container">
          <div className="ab-loader"></div>
          <p>{t('common.loading')}</p>
        </div>
      );
    }

    // If we are in edit mode but have no banner data after loading, show error or redirect
    if (view === 'edit' && !selectedBanner && !loading) {
      return (
        <div className="ab-error-container">
          <AlertCircle size={48} />
          <h3>{t('error.fetchFailed')}</h3>
          <button onClick={handleCancel} className="ab-back-btn">
            <ArrowLeft size={18} /> {t('cms.back_to_list')}
          </button>
        </div>
      );
    }

    return (
      <AppBannerForm 
        banner={selectedBanner}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <div className="ab-manager-container">
      <div className="ab-manager-header">
        <h2 className="ab-manager-title">{t("adminDashboard.menu.cms_banners")}</h2>
        <button className="ab-add-btn" onClick={() => navigate('/admin/dashboard/cms/banners/add')}>
          <Plus size={18} /> {t('common.addNew')}
        </button>
      </div>

      <div className="ab-manager-filters">
        <div className="ab-search-box">
          <Search className="ab-search-icon" size={18} />
          <input
            type="text"
            placeholder={t('common.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="ab-refresh-btn" onClick={fetchBanners} disabled={loading} title={t('common.refresh')}>
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="ab-table-card">
        {loading ? (
          <div className="ab-loading-state">
            <div className="ab-loader"></div>
            <p>{t('common.loading')}</p>
          </div>
        ) : filteredBanners.length > 0 ? (
          <div className="ab-table-scroll">
            <table className="ab-main-table">
              <thead>
                <tr>
                  <th>{t('common.image')}</th>
                  <th>{t('adminDashboard.cms.banner_title_en', 'Title (EN)')}</th>
                  <th>{t('adminDashboard.cms.banner_title_ar', 'Title (AR)')}</th>
                  <th>{t('adminDashboard.cms.country')}</th>
                  <th>{t('adminDashboard.cms.status')}</th>
                  <th>{t('adminDashboard.cms.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanners.map(banner => (
                  <tr key={banner.id}>
                    <td>
                      <div className="ab-preview-thumb">
                        <img src={resolveImageUrl(banner.image_url)} alt={banner.title_en || banner.title} />
                      </div>
                    </td>
                    <td className="ab-title-text">{banner.title_en || banner.title}</td>
                    <td className="ab-title-text" dir="rtl">{banner.title_ar || banner.title}</td>
                    <td>
                      <span className="ab-country-tag">
                        {banner.country_code === 'all' ? t('adminDashboard.menu.all') : banner.country_code}
                      </span>
                    </td>
                    <td>
                      <span className={`ab-status-pill ${banner.is_active ? 'active' : 'inactive'}`}>
                        {banner.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="ab-action-group">
                        <button className="ab-action-edit" onClick={() => handleEdit(banner)} title={t('common.edit')}>
                          <Edit size={16} />
                        </button>
                        <button className="ab-action-delete" onClick={() => handleDelete(banner.id)} title={t('common.delete')}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ab-empty-state">
            <AlertCircle className="ab-empty-icon" size={48} />
            <h3>{t('common.noData')}</h3>
            <p>{t('common.noResultsFound')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppBannerManager;
